import { useEffect, useMemo, useRef, useState } from 'react';

import type { PriorityPlaylist } from '@backrs_ui/common-ui';

import { useAppSelector, useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useDeletePlaylistMutation,
  useGetPlaylistByIdQuery,
  useGetTasksForPlaylistQuery,
  useGetPlaylistsQuery,
  useUpdateTaskOrderMutation,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.api';
import {
  openEditPlaylistDialog,
  selectCurrentPlaylistId,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';
import type { PriorityTask } from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';

import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Skeleton, SxProps, Theme } from '@mui/material';

import { TaskCard } from './TaskCard';
import { PlaylistViewDrawerHeader } from '../atoms/PlaylistViewDrawerHeader';
import {
  PLAYLIST_CARD_DRAWER_WIDTH,
  DRAWER_MAX_WIDTH,
  PLAYLIST_SKELETON_HEIGHT,
} from '../constants/priorities.constants';
import { usePlaylistTasksSort } from '../hooks/playlists.hooks';
import { useGetTaskActionsAndProperties } from '../hooks/tasks.hooks';
import { usePlaylistDnd } from '../hooks/usePlaylistDnd';

function mergeServerIntoLocalPreservingOrder(
  local: PriorityTask[] | null | undefined,
  server: PriorityTask[] | null | undefined,
): PriorityTask[] | null {
  if (!server) {
    return local ?? null;
  }
  if (!local || local.length === 0) {
    return server;
  }

  const serverMap = new Map<number, PriorityTask>(server.map((t) => [t.id, t]));
  // keep local order, but merge in server fields if present
  const merged = local.map((t) => {
    const s = serverMap.get(t.id);

    return s ? { ...t, ...s } : t;
  });

  return merged;
}

type PlaylistViewDrawerProps = Readonly<{
  handleClose: () => void;
}>;

export const PlaylistViewDrawer = ({ handleClose }: PlaylistViewDrawerProps) => {
  const dispatch = useAppDispatch();
  const [sortAnchorEl, setSortAnchorEl] = useState<HTMLElement | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const [localTasks, setLocalTasks] = useState<PriorityTask[] | null>(null);

  // NEW: prefer rendering the manual (local) order
  const [preferLocalOrder, setPreferLocalOrder] = useState(false);

  // track if user is in reorder mode
  const [isReorderMode, setIsReorderMode] = useState(false);

  // track which task is expanded
  const [expandedTaskId, setExpandedTaskId] = useState<number | null>(null);

  const currentPlaylistId = useAppSelector(selectCurrentPlaylistId);
  const { data: playlists } = useGetPlaylistsQuery(undefined);

  // Fallback: if not found in list (e.g., admin endpoint), fetch by id
  const { data: playlistById } = useGetPlaylistByIdQuery(
    { id: currentPlaylistId || 0 },
    {
      skip: !currentPlaylistId,
    },
  );

  const currentPlaylist =
    playlists?.find((playlist) => playlist.id === currentPlaylistId) || playlistById || null;

  const [deletePlaylist] = useDeletePlaylistMutation();
  const [updateTaskOrderRaw] = useUpdateTaskOrderMutation();

  const updateTaskOrder = (args: { playlistId: number; taskIds: number[] }) =>
    updateTaskOrderRaw(args).unwrap();

  const { data: tasksForPlaylist, isLoading: isLoadingTasksForPlaylist } =
    useGetTasksForPlaylistQuery(currentPlaylistId || 0, {
      skip: !currentPlaylistId,
    });

  const {
    taskTypes,
    taskStatuses,
    deleteTask,
    handleOpenEditTaskDialog,
    handleOpenNewTaskDialog,
    updateTask,
  } = useGetTaskActionsAndProperties();

  // Source-of-truth (with optimistic edits)
  const sourceTasks = localTasks ?? tasksForPlaylist ?? [];

  // Sorted view (used when user isn't insisting on manual order)
  const { sortedTasks, sortMenuItems, toggleSort } = usePlaylistTasksSort(sourceTasks);

  const listRef = useRef<HTMLDivElement | null>(null);

  // While dragging OR when the user has dragged (preferLocalOrder), render `localTasks` (unsorted).
  // Otherwise, use the sorted output.
  const visibleTasks = useMemo(() => {
    if (preferLocalOrder) return localTasks ?? tasksForPlaylist ?? [];

    return sortedTasks ?? [];
  }, [preferLocalOrder, localTasks, tasksForPlaylist, sortedTasks]);

  // Keep localTasks synced with server data when we are NOT preferring local and NOT dragging.
  // We mirror dragging from the DnD hook below.
  const [draggingFlag, setDraggingFlag] = useState(false);

  useEffect(() => {
    if (!tasksForPlaylist) {
      setLocalTasks(null);

      return;
    }

    setLocalTasks((current) => {
      // If we are preferring local (manual) order or dragging, keep local order
      // but merge in the latest fields (status, title changes, etc.)
      if (preferLocalOrder || draggingFlag) {
        return mergeServerIntoLocalPreservingOrder(current ?? [], tasksForPlaylist);
      }

      // Otherwise, accept server order wholesale
      return tasksForPlaylist;
    });
  }, [currentPlaylistId, tasksForPlaylist, preferLocalOrder, draggingFlag]);

  // ---- DnD hook -------------------------------------------------------------
  const { sensors, activeTask, dragging, onDragStart, onDragMove, onDragEnd } = usePlaylistDnd({
    visibleTasks,
    listRef,
    currentPlaylistId,
    updateTaskOrder,
    setLocalTasks,
    sourceTasks,
    autoscroll: { edgeThresholdPx: 96, stepPx: 12 },
    isReorderMode,
  });

  // mirror hook's dragging so effect above can consider it
  useEffect(() => {
    setDraggingFlag(dragging);
  }, [dragging]);

  // Force manual order when user starts a drag
  useEffect(() => {
    if (dragging) setPreferLocalOrder(true);
  }, [dragging]);

  // Wrap sort menu items so choosing a sort flips us back to sorted mode
  const wrappedSortMenuItems = useMemo(
    () =>
      sortMenuItems.map((item) => ({
        ...item,
        onClick: () => {
          setPreferLocalOrder(false); // return to sorted mode
          item.onClick?.();
        },
      })),
    [sortMenuItems],
  );

  if (!currentPlaylist) {
    return null;
  }

  const handleDeletePlaylist = () => {
    deletePlaylist(currentPlaylist.id);
    setConfirmDeleteOpen(false);
    handleClose();
  };

  const handleOpenEditPlaylistDialog = (playlist: PriorityPlaylist) => {
    dispatch(openEditPlaylistDialog(playlist));
  };

  const handleToggleReorderMode = () => {
    setIsReorderMode(!isReorderMode);
    // When entering reorder mode, switch to manual order
    if (!isReorderMode) {
      const tasksSortedByOrder = [...(tasksForPlaylist ?? [])].sort((a, b) => {
        if (a.order === null && b.order === null) return 0;
        if (a.order === null) return 1;
        if (b.order === null) return -1;

        return a.order - b.order;
      });

      setLocalTasks(tasksSortedByOrder);
      setPreferLocalOrder(true);
    }

    // Collapse any expanded task when toggling reorder mode
    setExpandedTaskId(null);
  };

  const handleTaskCardClick = (taskId: number) => {
    // Only allow expansion when reorder mode is off
    if (!isReorderMode) {
      setExpandedTaskId(expandedTaskId === taskId ? null : taskId);
    }
  };

  const percentageFilled =
    !!currentPlaylist.completed_tasks && !!currentPlaylist.total_tasks
      ? (currentPlaylist.completed_tasks / currentPlaylist.total_tasks) * 100
      : 0;

  return (
    <>
      <PlaylistViewDrawerHeader
        currentPlaylist={currentPlaylist}
        confirmDeleteOpen={confirmDeleteOpen}
        setConfirmDeleteOpen={setConfirmDeleteOpen}
        handleDeletePlaylist={handleDeletePlaylist}
        sortAnchorEl={sortAnchorEl}
        setSortAnchorEl={setSortAnchorEl}
        handleOpenNewTaskDialog={handleOpenNewTaskDialog}
        handleClose={handleClose}
        percentageFilled={percentageFilled}
        openEditPlaylistDialog={handleOpenEditPlaylistDialog}
        wrappedSortMenuItems={wrappedSortMenuItems}
        isReorderMode={isReorderMode}
        handleToggleReorderMode={handleToggleReorderMode}
      />

      {/* DND CONTEXT + LIST */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragMove={onDragMove}
        onDragEnd={async (evt) => {
          await onDragEnd(evt);
          // After a successful drop, keep showing manual order
          setPreferLocalOrder(true);

          // Force sort to 'order' when drag ends
          toggleSort('order');
        }}
      >
        <SortableContext
          items={visibleTasks.map((t) => t.id)}
          strategy={verticalListSortingStrategy}
        >
          <Box sx={playlistsContainerSx} ref={listRef}>
            {isLoadingTasksForPlaylist && (
              <>
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="rounded"
                    height={PLAYLIST_SKELETON_HEIGHT}
                    width={PLAYLIST_CARD_DRAWER_WIDTH}
                    animation="pulse"
                  />
                ))}
              </>
            )}

            {!isLoadingTasksForPlaylist && visibleTasks.length > 0 && (
              <>
                {visibleTasks.map((task) => (
                  <SortableRow key={task.id} task={task} isReorderMode={isReorderMode}>
                    <TaskCard
                      task={task}
                      restrictWidth={false}
                      isInSidebar={true}
                      isDraggable={isReorderMode}
                      deleteTask={deleteTask}
                      taskTypes={taskTypes}
                      taskStatuses={taskStatuses}
                      openEditTaskDialog={handleOpenEditTaskDialog}
                      handleDuplicateTask={(t) => handleOpenNewTaskDialog(currentPlaylist, true, t)}
                      updateTask={async (updatedPartial) => {
                        await updateTask({ ...updatedPartial, id: task.id });
                      }}
                      expanded={expandedTaskId === task.id}
                      onCardClick={() => handleTaskCardClick(task.id)}
                      isHighlighted={isReorderMode}
                    />
                  </SortableRow>
                ))}
              </>
            )}
          </Box>
        </SortableContext>

        {/* Drag Overlay */}
        <DragOverlay dropAnimation={null}>
          {activeTask ? (
            <Box sx={overlayContainerSx}>
              <TaskCard
                task={activeTask}
                restrictWidth={false}
                isInSidebar={true}
                isDraggable={isReorderMode}
                deleteTask={deleteTask}
                taskTypes={taskTypes}
                taskStatuses={taskStatuses}
                openEditTaskDialog={handleOpenEditTaskDialog}
                handleDuplicateTask={(t) => handleOpenNewTaskDialog(currentPlaylist, true, t)}
                updateTask={async (updatedTask) => {
                  await updateTask({ ...updatedTask, id: activeTask.id });
                }}
                expanded={expandedTaskId === activeTask.id}
                onCardClick={() => handleTaskCardClick(activeTask.id)}
                isHighlighted={isReorderMode}
              />
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>
    </>
  );
};

/**
 * Small inner component using useSortable (kept in-file).
 * Hides the in-list original while overlay is displayed.
 */
const SortableRow = ({
  task,
  children,
  isReorderMode,
}: {
  task: PriorityTask;
  children: React.ReactNode;
  isReorderMode: boolean;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    disabled: !isReorderMode,
  });

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...(isReorderMode ? listeners : {})}
      sx={getSortableRowSx(transform, transition, isDragging, isReorderMode)}
    >
      {children}
    </Box>
  );
};

const playlistsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  pt: backrs.spacing['spacing-xs'],
  minWidth: {
    xs: `calc(100vw - (${backrs.spacing['spacing-4xl']}))`,
    sm: `calc(${DRAWER_MAX_WIDTH}px - (${backrs.spacing['spacing-2xl']}))`,
  },
  px: backrs.spacing['spacing-sm'],
  pb: backrs.spacing['spacing-xl'],
  overflowY: 'auto',
  flex: 1,
  minHeight: 0,
});

const overlayContainerSx: SxProps<Theme> = {
  width: PLAYLIST_CARD_DRAWER_WIDTH,
};

const getSortableRowSx = (
  transform: { x: number; y: number; scaleX: number; scaleY: number } | null | undefined,
  transition: string | undefined,
  isDragging: boolean,
  isReorderMode: boolean,
): SxProps<Theme> => ({
  cursor: isReorderMode ? 'grab' : 'default',
  transform: CSS.Transform.toString(transform || null),
  transition,
  position: 'relative',
  // Hide the in-list original while overlay shows:
  opacity: isDragging ? 0 : 1,
  pointerEvents: isDragging ? 'none' : 'auto',
  '&:active': { cursor: isReorderMode ? 'grabbing' : 'default' },
});
